import { CollectionProductResponse } from 'api/product';
import { OneOffPurchaseInput } from 'api/subscriptions';
import { PdProductItem } from 'interfaces/line-item';
import { CollectionProduct, CollectionVariant } from 'models/collection/collection';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import { SubscriptionInterval } from '../constants/order';
import { SubscriptionStatus } from '../constants/subscription';
import { OneOffPurchaseItem, SubscriptionIntervalInDays } from '../interfaces/subscription';

/**
 * Returns the key for the translation related to the subscription status
 */
export const getSubscriptionStatusTranslationKey = (status: SubscriptionStatus): string => {
  switch (status) {
    case SubscriptionStatus.PAUSED:
      return 'account:subscriptions:paused';
    case SubscriptionStatus.CANCELLED:
      return 'account:subscriptions:cancelled';
    default:
      return 'account:subscriptions:active';
  }
};

/**
 * Converts the number into the format used to identify the subscription interval
 */
export const getSubscriptionIntervalByDaysCount = (
  intervalInDays: SubscriptionIntervalInDays
): SubscriptionInterval | undefined => {
  switch (intervalInDays) {
    case 14:
      return SubscriptionInterval.TWO_WEEKS;
    case 28:
      return SubscriptionInterval.FOUR_WEEKS;
    case 42:
      return SubscriptionInterval.SIX_WEEKS;
    case 56:
      return SubscriptionInterval.EIGHT_WEEKS;
    default:
      return undefined;
  }
};

/** Convert product to OneOffPurchaseInput  */
export const convertOneOffPurchaseItemToOneOffPurchaseInput = (
  product: OneOffPurchaseItem | PdProductItem
): OneOffPurchaseInput => {
  return {
    title: product.title,
    handle: product.handle || '',
    price: product.price,
    quantity: product.quantity,
    variantId: product.variantId + '',
    productId: product.productId + '',
    masterProductId: product.masterProductId,
    masterVariantId: product.masterVariantId + '',
    image: product.imageSrc,
  };
};

/** Convert collection product to OneOffPurchaseInput  */
export const convertCollectionProductToOneOffPurchaseInput = (
  product: CollectionProductResponse,
  variant: CollectionVariant,
  quantity: number
): OneOffPurchaseInput => {
  return {
    title: product.title,
    handle: product.handle || '',
    price: Number(variant.priceV2.amount.replace('.', '')),
    quantity,
    variantId: variant.id,
    productId: product.id + '',
    masterProductId: variant.id,
    masterVariantId: variant.id,
    image: product.image.src,
  };
};

/** Convert product to OneOffPurchaseInput  */
export const convertProductToSubscriptionItem = ({
  product,
  variantId,
  quantity,
}: {
  product: CollectionProduct | StaticPDPProduct;
  variantId: number;
  quantity: number;
}): OneOffPurchaseInput => {
  type CombinedVariants = CollectionVariant | Variant;
  const _variants = product.variants as Array<CombinedVariants>;

  const variant = _variants.find((v) => +v.id === variantId);

  function isCollectionVariant(variant: CombinedVariants): variant is CollectionVariant {
    return (variant as CollectionVariant).image.src !== undefined;
  }

  if (!variant) {
    throw new Error(`can't find variant with ${variantId}`);
  }

  const image = isCollectionVariant(variant) ? variant.image.src : variant.image.originalSrc;

  try {
    return {
      title: `${product.title} ${variant.title}`,
      handle: product.handle,
      /** we need to multiple value since api expects the value to be non decimal value (e.g 4.99 => 499 ) */
      /** hot fix. we should consider removing price property from the input to API */
      price: Number(variant.priceV2.amount.replace('.', '')),
      quantity,
      variantId: variantId + '',
      productId: product.id + '',
      masterProductId: product.id + '',
      masterVariantId: variantId + '',
      image,
    };
  } catch (error) {
    throw new Error('convertProductToSubscriptionItem error');
  }
};
